import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { SettingsService } from '../settings.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PermissionsService } from '../../shared/permissions/permissions.service';
import { PERMISSIONS } from 'edu-core';
import { ConfirmationDialogService } from '../../shared/confirmation-dialog/confirmation-dialog.service';
import { readFileWithTimeout } from 'src/app/shared/file-read.util';

/** WhatsApp's limit for video media; rejected server-side too. */
const MAX_VIDEO_BYTES = 16 * 1024 * 1024;

@Component({
  selector: 'edu-welcome-video-settings',
  standalone: false,
  templateUrl: './welcome-video-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './welcome-video-settings.component.scss',
})
export class WelcomeVideoSettingsComponent implements OnInit {
  @Input() orgId!: string;

  configured = false;
  /** Public URL of the organization's own video; empty while it is on the default one. */
  videoUrl = '';
  isLoading = false;
  isRemoving = false;
  uploadProgress = 0;
  /**
   * `reading` waits for the picked file's bytes to be fully available locally (guards
   * against stuck iCloud downloads / Google Drive virtual files), `initiating` is the
   * presigned URL request, `uploading` the bytes going straight to S3 (the only
   * measurable phase), `finalizing` the backend validating and publishing the object —
   * hence the indeterminate bar around a stuck 100% either side.
   */
  uploadPhase: 'idle' | 'reading' | 'initiating' | 'uploading' | 'finalizing' = 'idle';
  error: string | null = null;

  constructor(
    private settingsService: SettingsService,
    private snackBar: MatSnackBar,
    private permissions: PermissionsService,
    private confirmationDialog: ConfirmationDialogService,
  ) {}

  /** Writing these settings requires organization-settings.manage. */
  get canManage(): boolean {
    return this.permissions.has(PERMISSIONS.organizationSettings.manage);
  }

  get isUploading(): boolean {
    return this.uploadPhase !== 'idle';
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.settingsService.getWelcomeVideo(this.orgId).subscribe({
      next: (result) => {
        this.configured = result.configured;
        this.videoUrl = result.videoUrl ?? '';
        this.isLoading = false;
      },
      error: () => (this.isLoading = false),
    });
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Clear immediately so re-picking the same file after an error still fires.
    input.value = '';
    if (!file || !this.canManage || this.isUploading) {
      return;
    }

    if (!file.type.startsWith('video/')) {
      this.error = 'The welcome video must be a video file.';
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      this.error = 'The welcome video must be 16 MB or smaller.';
      return;
    }

    this.error = null;
    this.uploadProgress = 0;

    try {
      // 0. Read the file fully into memory up front, guarding against Google Drive
      // "virtual file" races on Android and stuck iCloud downloads on iOS/iPadOS Safari.
      this.uploadPhase = 'reading';
      const readyFile = await readFileWithTimeout(file, 60000);

      // 1. Ask the backend for a presigned URL and the key it was minted for.
      this.uploadPhase = 'initiating';
      const { url, key } = await this.settingsService
        .getWelcomeVideoUploadUrl(this.orgId, readyFile.name, readyFile.type)
        .toPromise();

      // 2. Upload the video straight to S3, surfacing granular progress.
      this.uploadPhase = 'uploading';
      await new Promise<void>((resolve, reject) => {
        this.settingsService.uploadWelcomeVideoToS3(url, readyFile).subscribe({
          next: (progress) => (this.uploadProgress = progress),
          error: reject,
          complete: resolve,
        });
      });

      // 3. Hand it to the backend, which validates and publishes it before saving.
      this.uploadPhase = 'finalizing';
      const saved = await this.settingsService
        .saveWelcomeVideo(this.orgId, key, readyFile.type)
        .toPromise();

      // Only reflect the new video once the backend confirms it was saved.
      this.configured = true;
      this.videoUrl = saved.videoUrl;
      this.snackBar.open('✅ Welcome video updated', 'Dismiss', {
        duration: 3000,
      });
    } catch (err) {
      this.error = this.messageOf(err);
    } finally {
      this.uploadPhase = 'idle';
      this.uploadProgress = 0;
    }
  }

  async removeVideo(): Promise<void> {
    if (!this.canManage || this.isUploading || this.isRemoving) {
      return;
    }

    // The stored video is deleted, not just unlinked, so there is no undo.
    const confirmed = await this.confirmationDialog.confirm({
      title: 'Remove welcome video',
      message:
        'Are you sure you want to remove your welcome video? New students and their ' +
        'parents will receive the default one instead. This cannot be undone.',
      confirmText: 'Remove',
      severity: 'danger',
    });

    if (!confirmed) {
      return;
    }

    this.error = null;
    this.isRemoving = true;
    try {
      await this.settingsService.removeWelcomeVideo(this.orgId).toPromise();
      this.configured = false;
      this.videoUrl = '';
      this.snackBar.open('Welcome video removed — using the default', 'Dismiss', {
        duration: 3000,
      });
    } catch (err) {
      this.error = this.messageOf(err);
    } finally {
      this.isRemoving = false;
    }
  }

  /** Surfaces the backend's message (e.g. the WhatsApp size limit) rather than a bare status. */
  private messageOf(err: unknown): string {
    const backendMessage = (err as HttpErrorResponse)?.error?.message;
    if (typeof backendMessage === 'string' && backendMessage.length > 0) {
      return backendMessage;
    }
    return (err as Error)?.message || 'Failed to upload the welcome video.';
  }
}
